import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AgeVerification from '../components/AgeVerification';
import CookieConsent from '../components/CookieConsent';
import { FileText, Scale, AlertTriangle, Users, Shield, Gavel } from 'lucide-react';

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-book-cream">
      <Header />
      <main className="book-section">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center space-x-4 mb-4">
              <div className="w-16 h-16 bg-book-brown rounded-full flex items-center justify-center">
                <FileText className="h-8 w-8 text-book-cream" />
              </div>
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-book-brown mb-2">
                  Terms & Conditions
                </h1>
                <p className="text-lg text-book-brown-dark">
                  Legal framework governing your use of Brit Ember services and platform access
                </p>
              </div>
            </div>
            <div className="bg-book-beige rounded-lg p-4 text-sm text-book-brown-dark">
              <strong>Last Updated:</strong> 16 December 2025 | <strong>Effective Date:</strong> 1 January 2026
            </div>
          </div>

          {/* Introduction */}
          <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed mb-12">
            <p className="mb-6">
              These Terms & Conditions establish the legal framework governing your access to and use of Brit Ember's
              casino aggregation services. By accessing our platform, you acknowledge and agree to be bound by these
              terms, which are designed to protect both your interests and those of our service. Our terms reflect
              our commitment to fair play, responsible gaming, and regulatory compliance within the UK gambling market.
            </p>

            <p className="mb-6">
              Brit Ember operates as an independent casino aggregator, providing information, reviews, and access
              to licensed gaming platforms. We are not a gambling operator ourselves, but rather a service that
              connects users with reputable casino providers. These terms ensure that all interactions occur within
              a framework of transparency, fairness, and legal compliance.
            </p>
          </div>

          {/* Main Content Sections */}
          <div className="space-y-12">
            {/* Section 1 */}
            <div className="bg-white rounded-xl shadow-book p-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 bg-book-brown rounded-lg flex items-center justify-center">
                  <Users className="h-6 w-6 text-book-cream" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-book-brown mb-3">User Eligibility and Age Restrictions</h2>
                  <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
                    <p className="mb-4">
                      Access to Brit Ember services is strictly limited to individuals who meet our eligibility criteria
                      and comply with all applicable legal requirements. Our platform is designed exclusively for
                      responsible adults who understand the nature of casino gaming.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Age Requirements</h3>
                    <ul className="list-disc pl-6 space-y-2 mb-4">
                      <li>You must be at least 18 years of age (or the legal gambling age in your jurisdiction)</li>
                      <li>You must be capable of forming legally binding agreements</li>
                      <li>You must not be excluded from gambling under any applicable laws</li>
                      <li>You must reside in a jurisdiction where online gambling is legal and regulated</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Eligibility Verification</h3>
                    <p className="mb-4">
                      We reserve the right to verify your eligibility at any time and may request documentation to
                      confirm your age, identity, and location. Failure to provide satisfactory verification may result
                      in immediate suspension or termination of your account access. We comply fully with UK age
                      verification regulations and cooperate with relevant authorities when required.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Geographic Restrictions</h3>
                    <p className="mb-4">
                      Brit Ember services are primarily intended for users in the United Kingdom. Users from other
                      jurisdictions may access our informational content, but casino recommendations are specifically
                      tailored for the UK market. Users are responsible for ensuring compliance with local laws
                      regarding online gambling and casino access.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2 */}
            <div className="bg-white rounded-xl shadow-book p-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 bg-book-brown rounded-lg flex items-center justify-center">
                  <Scale className="h-6 w-6 text-book-cream" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-book-brown mb-3">Service Description and User Obligations</h2>
                  <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
                    <p className="mb-4">
                      Brit Ember provides casino aggregation services, connecting users with licensed gaming platforms
                      through informational content, reviews, and promotional opportunities. Our service is informational
                      in nature and does not involve direct gambling operations.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Service Scope</h3>
                    <ul className="list-disc pl-6 space-y-2 mb-4">
                      <li>Casino platform reviews and comparisons</li>
                      <li>Promotional offer information and bonus details</li>
                      <li>Educational content on responsible gaming</li>
                      <li>Access to licensed casino platforms through affiliate links</li>
                      <li>Customer support for platform-related inquiries</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">User Responsibilities</h3>
                    <ul className="list-disc pl-6 space-y-2 mb-4">
                      <li>Provide accurate and current information</li>
                      <li>Maintain the security of your account credentials</li>
                      <li>Use our services in accordance with applicable laws</li>
                      <li>Respect the rights and privacy of other users</li>
                      <li>Report any suspicious or illegal activities</li>
                      <li>Accept responsibility for all gambling activities</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Prohibited Activities</h3>
                    <p className="mb-4">
                      Users are strictly prohibited from engaging in any activities that violate these terms, applicable
                      laws, or harm the integrity of our service. This includes but is not limited to fraudulent activities,
                      harassment of other users, attempts to manipulate our systems, or use of our platform for illegal purposes.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3 */}
            <div className="bg-white rounded-xl shadow-book p-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 bg-book-brown rounded-lg flex items-center justify-center">
                  <Gavel className="h-6 w-6 text-book-cream" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-book-brown mb-3">Affiliate Relationships and Financial Terms</h2>
                  <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
                    <p className="mb-4">
                      Brit Ember operates affiliate relationships with licensed casino operators. These partnerships
                      enable us to provide comprehensive services while maintaining editorial independence and user trust.
                      Our affiliate arrangements are structured to benefit both our users and our service sustainability.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Affiliate Disclosure</h3>
                    <p className="mb-4">
                      We clearly disclose all affiliate relationships and compensation arrangements. Links to casino
                      platforms may include affiliate codes that provide us with commission on successful registrations
                      or deposits. This compensation does not influence our editorial decisions, platform rankings,
                      or recommendations. Our reviews and ratings are based solely on merit and user experience.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Commission Structure</h3>
                    <p className="mb-4">
                      Affiliate commissions are earned only when users successfully register and make qualifying deposits
                      at recommended casinos. We do not receive compensation for unsuccessful registrations or inactive
                      accounts. Commission rates vary by casino and are standard industry arrangements that do not
                      affect our service quality or user recommendations.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Financial Disclaimers</h3>
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                      <div className="flex">
                        <AlertTriangle className="h-5 w-5 text-yellow-400 mr-2" />
                        <div>
                          <p className="text-sm text-yellow-800">
                            <strong>Important:</strong> Brit Ember does not provide financial advice. All gambling activities
                            carry financial risk. Users are responsible for their own financial decisions and should gamble
                            only with money they can afford to lose. Our service does not guarantee winnings or financial returns.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 4 */}
            <div className="bg-white rounded-xl shadow-book p-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 bg-book-brown rounded-lg flex items-center justify-center">
                  <Shield className="h-6 w-6 text-book-cream" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-book-brown mb-3">Responsible Gaming and Player Protection</h2>
                  <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
                    <p className="mb-4">
                      Responsible gaming represents our highest priority and fundamental commitment. We integrate
                      responsible gaming principles throughout our service and actively promote safe, sustainable
                      gambling practices. Our platform includes multiple tools and resources to support responsible gaming.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Self-Assessment Tools</h3>
                    <ul className="list-disc pl-6 space-y-2 mb-4">
                      <li>Regular reality checks and session limit reminders</li>
                      <li>Deposit limit setting and enforcement</li>
                      <li>Loss limit configuration and monitoring</li>
                      <li>Time-on-site tracking and break suggestions</li>
                      <li>Self-exclusion options and account closure procedures</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Support Resources</h3>
                    <ul className="list-disc pl-6 space-y-2 mb-4">
                      <li>Access to professional gambling support organizations</li>
                      <li>Educational content on responsible gaming practices</li>
                      <li>Emergency contact information for immediate assistance</li>
                      <li>Links to certified treatment and counseling services</li>
                      <li>Information on self-exclusion schemes and cooling-off periods</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Regulatory Compliance</h3>
                    <p className="mb-4">
                      We comply fully with UK Gambling Commission regulations regarding responsible gaming, age verification,
                      and player protection. Our service includes mandatory age verification, problem gambling support,
                      and cooperation with regulatory authorities. Users who appear to be at risk receive appropriate
                      interventions and support referrals.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 5 */}
            <div className="bg-white rounded-xl shadow-book p-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 bg-book-brown rounded-lg flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-book-cream" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-book-brown mb-3">Limitation of Liability and Disclaimers</h2>
                  <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
                    <p className="mb-4">
                      While we strive to provide accurate, reliable, and helpful information, users must understand
                      the inherent limitations and risks associated with online casino gaming. Our service provides
                      guidance and information, but ultimate responsibility rests with individual users.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Service Limitations</h3>
                    <ul className="list-disc pl-6 space-y-2 mb-4">
                      <li>Information is provided for general guidance and may not be current</li>
                      <li>Casino terms and offers are subject to change without notice</li>
                      <li>Platform availability may vary by jurisdiction and user location</li>
                      <li>We cannot guarantee the accuracy of third-party casino information</li>
                      <li>Service interruptions may occur due to technical issues or maintenance</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Financial Risk Disclaimer</h3>
                    <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
                      <p className="text-red-800">
                        <strong>Gambling involves financial risk.</strong> All casino games carry the potential for financial loss.
                        Users should only gamble with money they can afford to lose. Past performance does not guarantee
                        future results. Brit Ember does not guarantee winnings or provide financial advice. Gambling
                        should be viewed as entertainment, not as a source of income.
                      </p>
                    </div>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Third-Party Content</h3>
                    <p className="mb-4">
                      Our platform contains links to third-party casino operators and affiliate websites. We are not
                      responsible for the content, policies, or practices of these external sites. Users access third-party
                      platforms at their own risk and should review each operator's terms and conditions independently.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 6 */}
            <div className="bg-white rounded-xl shadow-book p-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 bg-book-brown rounded-lg flex items-center justify-center">
                  <FileText className="h-6 w-6 text-book-cream" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-book-brown mb-3">Account Termination and Service Modification</h2>
                  <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
                    <p className="mb-4">
                      We reserve the right to modify, suspend, or terminate our services at any time for operational,
                      regulatory, or business reasons. Users may also terminate their engagement with our platform
                      at any time. Account termination procedures ensure proper handling of user data and final settlements.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Service Termination</h3>
                    <p className="mb-4">
                      Brit Ember may terminate or suspend your access to our services immediately, without prior notice
                      or liability, for any reason including breach of these terms, illegal activity, or at our sole discretion.
                      Upon termination, your right to use our services ceases immediately.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Data Retention</h3>
                    <p className="mb-4">
                      Following account termination, we will retain your personal data as required by law and for
                      legitimate business purposes. Data retention periods comply with UK data protection regulations
                      and our internal data management policies. Users may request data deletion subject to legal
                      requirements and outstanding obligations.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Terms Modification</h3>
                    <p className="mb-4">
                      We reserve the right to modify these terms at any time. Material changes will be communicated
                      through our platform or direct notification. Continued use of our services after changes become
                      effective constitutes acceptance of the modified terms.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-book-beige rounded-xl p-8 shadow-book-inset mt-12">
            <h2 className="text-2xl font-bold text-book-brown mb-6 text-center">Questions About These Terms?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="text-center">
                <h3 className="font-semibold text-book-brown mb-2">Legal Department</h3>
                <p className="text-book-brown-dark mb-1">legal@britember.com</p>
                <p className="text-sm text-book-brown-dark">For terms and legal inquiries</p>
              </div>
              <div className="text-center">
                <h3 className="font-semibold text-book-brown mb-2">Customer Support</h3>
                <p className="text-book-brown-dark mb-1">support@britember.com</p>
                <p className="text-sm text-book-brown-dark">For service-related questions</p>
              </div>
            </div>
            <div className="mt-6 text-center">
              <p className="text-sm text-book-brown-dark">
                These terms are governed by English law and subject to the exclusive jurisdiction of the English courts.
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <AgeVerification />
      <CookieConsent />
    </div>
  );
};

export default TermsConditions;
